import { useEffect, useState } from "react";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import GalleryModalWrapper from "./galleryModalWrapper";

interface GalleryItem {
  id: string;
  src: string;
  name: string;
  alt?: string;
  thumbnail?: string;
}

interface CustomGalleryViewProps {
  show: boolean;
  handleClose: () => void;
  items: GalleryItem[];
  initialIndex?: number;
}

const CustomGalleryView = ({
  show,
  handleClose,
  items,
  initialIndex = 0,
}: CustomGalleryViewProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
      } else if (e.key === "ArrowRight") {
        setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
      } else if (e.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [show, items.length, handleClose]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
  };

  if (!show || items.length === 0) return null;

  const currentItem = items[currentIndex];

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <GalleryModalWrapper>
      <div className={styles.galleryContainer}>
        <div className={styles.galleryContent}>
          <div
            className={classNames(
              styles.imgWrapper,
              "w-full h-full flex justify-center items-center relative",
            )}
          >
            <div className={classNames(styles.mainImageContainer, "relative")}>
              {currentItem.src ? (
                <img
                  src={currentItem.src}
                  alt={currentItem.alt || `Gallery item ${currentIndex + 1}`}
                  className={styles.mainImage}
                />
              ) : (
                <div className={styles.placeholder}>No image available</div>
              )}

              <div
                className={classNames(
                  styles.imageHeader,
                  "absolute -top-12 left-0 flex items-center justify-between w-full px-16",
                )}
              >
                <div className=""></div>
                <div className={classNames(styles.counter)}>
                  {currentIndex + 1} of {items.length}
                </div>
                <button
                  className={classNames(styles.closeButton)}
                  onClick={handleClose}
                  aria-label="Close gallery"
                >
                  <Icons.Cross className={styles.closeIcon} />
                </button>
              </div>
            </div>
            <div className="absolute xl:-bottom-24 lg:-bottom-20 md:-bottom-16 -bottom-12 left-0 flex justify-center items-center w-full">
              <span className={styles.imageName}>{currentItem.name}</span>
            </div>
            {items.length > 1 && (
              <button
                className={classNames(styles.navButton, styles.navButtonLeft)}
                onClick={handlePrevious}
                aria-label="Previous image"
              >
                <Icons.ChevLeft className={styles.navIcon} />
              </button>
            )}
            {items.length > 1 && (
              <button
                className={classNames(styles.navButton, styles.navButtonRight)}
                onClick={handleNext}
                aria-label="Next image"
              >
                <Icons.ChevRight className={styles.navIcon} />
              </button>
            )}
          </div>
        </div>

        <div className={styles.mobileGalleryContainer}>
          <div className={styles.mobileTopSection}>
            <div className={styles.mobileCounter}>
              {currentIndex + 1} of {items.length}
            </div>
            <button
              className={styles.mobileCloseButton}
              onClick={handleClose}
              aria-label="Close gallery"
            >
              <Icons.Cross className={styles.closeIcon} />
            </button>
          </div>

          <div className={styles.mobileMiddleSection}>
            {currentItem.src ? (
              <img
                src={currentItem.src}
                alt={currentItem.alt || `Gallery item ${currentIndex + 1}`}
                className={styles.mobileMainImage}
              />
            ) : (
              <div className={styles.placeholder}>No image available</div>
            )}
          </div>

          <div className={styles.mobileBottomSection}>
            <div className={styles.mobileImageName}>{currentItem.name}</div>
            <div className={styles.thumbnailCarousel}>
              {items.map((item, index) => (
                <button
                  key={item.id || index}
                  className={classNames(
                    styles.thumbnailButton,
                    index === currentIndex && styles.thumbnailButtonActive,
                  )}
                  onClick={() => handleThumbnailClick(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img
                    src={item.thumbnail || item.src}
                    alt={item.alt || `Thumbnail ${index + 1}`}
                    className={styles.thumbnailImage}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </GalleryModalWrapper>
  );
};

export default CustomGalleryView;
